import { useState, useCallback, useEffect, useRef } from 'react';
import { Keyboard } from '@capacitor/keyboard';
import { Capacitor } from '@capacitor/core';
import { isCapacitor } from '../utils/platform';

/**
 * Hook for managing fullscreen text editor on Capacitor and web
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether to enable fullscreen editing (default: true for Capacitor, true for web)
 * @param {Function} options.onSave - Callback when text is saved
 * @param {string} options.title - Title for the editor
 * @param {string} options.placeholder - Placeholder text
 * @param {boolean} options.showCharCount - Whether to show character count
 * @param {number} options.maxLength - Maximum character length
 * @param {boolean} options.adjustForKeyboard - Whether to adjust view for keyboard (default: true)
 * @returns {Object} Hook state and methods
 */
export const useFullScreenTextEditor = (options = {}) => {
  const {
    enabled = true, // Enable for both web and Capacitor
    onSave,
    title = 'Modifier le texte',
    placeholder = 'Entrez votre texte ici...',
    showCharCount = true,
    maxLength = null,
    adjustForKeyboard = true,
  } = options;

  const [isOpen, setIsOpen] = useState(false);
  const [initialValue, setInitialValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const isIOS = useRef(Capacitor.getPlatform() === 'ios');

  // Handle keyboard show/hide events (Capacitor only)
  useEffect(() => {
    if (!enabled || !adjustForKeyboard || !isOpen || !isCapacitor) return;

    let showListener;
    let hideListener;

    const setupKeyboardListeners = async () => {
      // For iOS, use different event names and handle viewport changes
      if (isIOS.current) {
        // Set keyboard accessory bar and resize mode for iOS
        await Keyboard.setAccessoryBarVisible({ isVisible: false });
        await Keyboard.setResizeMode({ mode: 'none' });
        
        showListener = await Keyboard.addListener('keyboardWillShow', (info) => {
          setKeyboardHeight(info.keyboardHeight);
          // Adjust viewport for iOS
          if (window.visualViewport) {
            const handleViewportChange = () => {
              const height = window.innerHeight - window.visualViewport.height;
              setKeyboardHeight(height > 0 ? height : info.keyboardHeight);
            };
            window.visualViewport.addEventListener('resize', handleViewportChange);
          }
        });

        hideListener = await Keyboard.addListener('keyboardWillHide', () => {
          setKeyboardHeight(0);
        });
      } else {
        // Android keyboard handling
        showListener = await Keyboard.addListener('keyboardDidShow', (info) => {
          setKeyboardHeight(info.keyboardHeight);
        });

        hideListener = await Keyboard.addListener('keyboardDidHide', () => {
          setKeyboardHeight(0);
        });
      }
    };

    setupKeyboardListeners();

    return () => {
      if (showListener) showListener.remove();
      if (hideListener) hideListener.remove();
    };
  }, [enabled, adjustForKeyboard, isOpen]);

  // iOS-specific: Force keyboard to show when editor opens
  useEffect(() => {
    if (isOpen && isIOS.current && enabled && isCapacitor) {
      // Small delay to ensure the editor is rendered
      const timer = setTimeout(() => {
        // Find and focus the textarea/input in your editor component
        const input = document.querySelector('.fullscreen-editor textarea, .fullscreen-editor input');
        if (input) {
          input.focus();
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isOpen, enabled]);

  const openEditor = useCallback((value = '') => {
    if (!enabled) return false;

    setInitialValue(value);
    setIsOpen(true);
    
    // For iOS, ensure the keyboard will show
    if (isCapacitor && isIOS.current) {
      Keyboard.show();
    }
    
    return true;
  }, [enabled]);

  const closeEditor = useCallback(() => {
    setIsOpen(false);
    setInitialValue('');
    setLoading(false);
    setKeyboardHeight(0);
    
    // Hide keyboard when closing editor
    if (isCapacitor && enabled) {
      Keyboard.hide();
    }
  }, [enabled]);

  const handleSave = useCallback(async (value) => {
    if (!onSave) {
      closeEditor();
      return;
    }

    setLoading(true);
    try {
      await onSave(value);
      closeEditor();
    } catch (error) {
      // Let the editor component handle the error display
      throw error;
    } finally {
      setLoading(false);
    }
  }, [onSave, closeEditor]);

  const editorProps = {
    isOpen,
    onClose: closeEditor,
    onSave: handleSave,
    initialValue,
    title,
    placeholder,
    showCharCount,
    maxLength,
    loading,
    autofocus: true,
    keyboardHeight, // Pass keyboard height to editor component
    isIOS: isIOS.current,
  };

  return {
    isOpen,
    openEditor,
    closeEditor,
    editorProps,
    isEnabled: enabled,
    keyboardHeight,
  };
};

/**
 * Hook for textarea that opens fullscreen editor on focus (Capacitor and web)
 * @param {Object} options - Configuration options
 * @param {string} options.value - Current value of the textarea
 * @param {Function} options.onChange - Callback when value changes
 * @param {Function} options.onFocus - Additional focus handler
 * @param {Object} options.editorOptions - Options to pass to useFullScreenTextEditor
 * @returns {Object} Props to spread on textarea and editor state
 */
export const useFullScreenTextarea = (options = {}) => {
  const {
    value = '',
    onChange,
    onFocus,
    editorOptions = {},
  } = options;

  const handleEditorSave = useCallback(async (newValue) => {
    if (onChange) {
      onChange(newValue);
    }
  }, [onChange]);

  const {
    openEditor,
    editorProps: fullEditorProps,
    isEnabled,
  } = useFullScreenTextEditor({
    ...editorOptions,
    onSave: handleEditorSave,
  });

  const handleTextareaFocus = useCallback((e) => {
    if (isEnabled) {
      // Blur to prevent native keyboard on Capacitor
      if (isCapacitor && e.target) {
        e.target.blur();
      }
      openEditor(value);
    }

    // Call additional focus handler if provided
    if (onFocus) {
      onFocus(e);
    }
  }, [isEnabled, openEditor, value, onFocus]);

  const handleTextareaClick = useCallback((e) => {
    if (isEnabled) {
      e.preventDefault();
      handleTextareaFocus(e);
    }
  }, [isEnabled, handleTextareaFocus]);

  const textareaProps = {
    value,
    onChange: isEnabled ? undefined : (e) => onChange(e.target.value),
    onFocus: handleTextareaFocus,
    onClick: handleTextareaClick,
    readOnly: isEnabled && isCapacitor, // Only readOnly on Capacitor
  };

  return {
    textareaProps,
    editorProps: fullEditorProps,
    isFullScreenEnabled: isEnabled,
  };
};